// src/components/UserProfile/SignInMethods.jsx
// "Formas de entrar" — unifica en UN lugar las llaves de acceso de la cuenta (email + wallet)
// para dejar claro el modelo: UNA sola cuenta, varias llaves. Entrás con la que quieras y sumás
// la otra para respaldo/recuperación. NO son cuentas distintas.
// Reusa los componentes existentes (se auto-ocultan según lo que ya tengas):
//   Email  → AddEmail (si no tiene) + VerifyEmailPrompt (si no verificó)
//   Wallet → WalletLink (si no tiene) + WalletRecovery + WalletExport
import React from 'react';
import { FaEnvelope, FaWallet, FaKey } from 'react-icons/fa';
import { useAuth } from '../../Context/AuthContext';
import AddEmail from '../VerifyEmail/AddEmail';
import VerifyEmailPrompt from '../VerifyEmail/VerifyEmailPrompt';
import WalletLink from '../WalletSecurity/WalletLink';
import WalletRecovery from '../WalletSecurity/WalletRecovery';
import WalletExport from '../WalletSecurity/WalletExport';
import WalletCard from '../WalletSecurity/WalletCard';

const PRIVY_ENABLED = !!process.env.REACT_APP_PRIVY_APP_ID;

function Status({ on, okText, offText }) {
  return (
    <span style={{
      fontSize: '0.64rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6,
      background: on ? 'rgba(0,212,18,0.12)' : 'var(--mk-bg-card, #f0f2f5)',
      color: on ? '#00860c' : 'var(--mk-text-tertiary, #8a9bb0)',
    }}>{on ? okText : offText}</span>
  );
}

export default function SignInMethods() {
  const { user } = useAuth();
  const hasEmail       = !!user?.email;
  const hasWallet      = !!user?.walletAddress;
  const emailVerified  = !!user?.emailVerified;

  const rowHead = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  const mono    = { fontSize: '0.74rem', color: 'var(--mk-text-secondary)', fontFamily: 'var(--fin-font-mono, monospace)' };

  return (
    <section style={{ marginBottom: '1.25rem', background: 'var(--mk-bg-card, #fff)', border: '1px solid var(--mk-border, #e2e8f0)', borderRadius: 14, padding: '1.1rem 1.15rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <FaKey style={{ color: '#00b248' }} />
        <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--mk-text-primary)' }}>Formas de entrar</h3>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: '0.8rem', color: 'var(--mk-text-secondary)', lineHeight: 1.5 }}>
        Una sola cuenta, varias llaves. Entrás con la que quieras — sumá la otra para <b>respaldo y recuperación</b>.
        No son cuentas distintas: es tu misma identidad.
      </p>

      {/* EMAIL */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: PRIVY_ENABLED ? 14 : 0, borderBottom: PRIVY_ENABLED ? '1px solid var(--mk-border-light, #eef1f4)' : 'none', marginBottom: PRIVY_ENABLED ? 14 : 0 }}>
        <div style={rowHead}>
          <FaEnvelope style={{ color: 'var(--mk-text-tertiary)' }} />
          <b style={{ fontSize: '0.86rem', color: 'var(--mk-text-primary)' }}>Email</b>
          <Status on={hasEmail} okText={emailVerified ? '✓ verificado' : 'sin verificar'} offText="no vinculado" />
          {hasEmail && <span style={mono}>{user.email}</span>}
        </div>
        <AddEmail />
        <VerifyEmailPrompt />
      </div>

      {/* WALLET */}
      {PRIVY_ENABLED && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={rowHead}>
            <FaWallet style={{ color: '#00b248' }} />
            <b style={{ fontSize: '0.86rem', color: 'var(--mk-text-primary)' }}>Wallet (autocustodia)</b>
            <Status on={hasWallet} okText="✓ vinculada" offText="no vinculada" />
          </div>
          {hasWallet && <WalletCard />}
          <WalletLink />
          <WalletRecovery />
          <WalletExport />
        </div>
      )}
    </section>
  );
}
